import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import {
  ArrowForwardIos,
  CheckCircle,
  EditOutlined,
  DeleteOutline,
} from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import colors from '../../../../../themes/main-colors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bold: {
      fontWeight: theme.typography.fontWeightBold,
    },
    cardDetails: {
      paddingLeft: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(0),
      },
    },
    title: {
      marginBottom: theme.spacing(1),
    },
    card: (props: { isEnrolled: boolean }) => ({
      padding: props.isEnrolled
        ? theme.spacing(3.5, 3.5, 1.5, 3.5)
        : theme.spacing(3.5),
      margin: theme.spacing(1, 0),
      cursor: props.isEnrolled ? 'default' : 'pointer',
    }),
    enrolled: {
      border: '1px solid ' + colors.sparkBlue,
      background: colors.aliceBlue,
      boxShadow: '0px 3px 6px ' + colors.boxShadowColor,
    },
    notEnrolled: {
      border: '1px solid ' + colors.lightGray1,
    },
    sparkBlue: {
      color: colors.sparkBlue,
    },
    footer: {
      width: '100%',
      borderTop: '1px solid #70BCFB',
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
    alignCenter: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: '5px',
      },
    },
    cursorPointer: {
      cursor: 'pointer',
    },
  }),
);

interface Props {
  title: string;
  description: string | React.ReactNode;
  isEnrolled: boolean;
  icon?: string | React.ReactNode;
  isDeleteEnabled?: boolean;
  enrolledText?: string;
  viewProgramDetails: () => void;
  onCancel: () => void;
}

export default ({
  title,
  description,
  icon,
  isEnrolled = false,
  isDeleteEnabled = true,
  enrolledText= 'Enrolled',
  viewProgramDetails,
  onCancel,
}: Props) => {
  const classes = useStyles({ isEnrolled });

  const showProgramInfo = () => {
    if (isEnrolled) {
      return false;
    }

    viewProgramDetails();
  };

  return (
    <>
      <Grid
        container
        item
        className={clsx({
          [classes.card]: true,
          [classes.enrolled]: isEnrolled,
          [classes.notEnrolled]: !isEnrolled,
        })}
        onClick={showProgramInfo}
        data-testid="program-card"
      >
        <Grid item container spacing={0}>
          <Grid item container xs={3} md={2} justify="center">
            <Box pt={2}>{icon}</Box>
          </Grid>

          <Grid item container xs={12} md={10} className={classes.cardDetails}>
            <Grid
              item
              container
              justify="space-between"
              className={classes.title}
            >
              <Box pt={1}>
                <Typography
                  variant="h2"
                  className={clsx(classes.sparkBlue, classes.bold)}
                >
                  {title}
                </Typography>
              </Box>
              {!isEnrolled && (
                <Box pt={1}>
                  <ArrowForwardIos className={classes.sparkBlue} />
                </Box>
              )}
            </Grid>

            <Typography variant="body2">{description}</Typography>
          </Grid>
        </Grid>
        {isEnrolled && (
          <Grid
            item
            container
            justify="space-between"
            className={classes.footer}
            data-testid="program-enrolled"
          >
            <Box pt={1}>
              <Typography
                variant="h4"
                className={clsx(classes.sparkBlue, classes.alignCenter)}
              >
                <CheckCircle htmlColor="#4DA189" />
                <strong>{enrolledText}</strong>
              </Typography>
            </Box>
            <Box pt={1}>
              <EditOutlined
                htmlColor={colors.sparkBlue}
                onClick={viewProgramDetails}
                className={classes.cursorPointer}
              />
              {isDeleteEnabled && <DeleteOutline
                htmlColor={colors.sparkBlue}
                className={classes.cursorPointer}
                onClick={onCancel}
                data-testid="cancel-icon"
              />}
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
};
